/**
 * useAdvertisementAnalytics.js
 *
 * Analytics hook for the Advertisement callback-request form
 * (registerCallback → the compact "Book Your Free Consultation" card).
 *
 * Mirrors the exact pattern of useContactAnalytics.js:
 *   - IS_PRODUCTION guard excludes localhost + 127.0.0.1
 *   - Dev mode: console.group + console.table with [AdvertisementAnalytics] prefix
 *   - react-ga4 used in production
 *
 * NOTE ON PAGEVIEWS: the initial `page_view` hit for every route — including
 * direct landings on /freeConsultation — is already sent once by the
 * app-level mount effect in App.js:
 *
 *   useEffect(() => {
 *     if (IS_PRODUCTION) {
 *       ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
 *     }
 *   }, []);
 *
 * This hook intentionally does NOT send its own pageview hit, to avoid
 * double-counting. It only sends the custom form-specific events below.
 *
 * HOW TO USE:
 *   const analytics = useAdvertisementAnalytics();
 *   analytics.trackFormView(window.location.href);
 *   analytics.trackFieldFocus("mobile");
 *   analytics.trackSubmitAttempt(hasErrors);
 *   analytics.trackSubmitSuccess();
 *   analytics.trackSubmitError("network");
 *
 * DEV MODE:
 *   Open browser DevTools → Console to see all events grouped under
 *   [AdvertisementAnalytics] in real time.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[AdvertisementAnalytics] ${eventName}`, "color:#C1662E;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useAdvertisementAnalytics = () => {

  // ── Page / section view ───────────────────────────────────────────────────────

  /**
   * @param {string} url
   */
  const trackFormView = useCallback((url) => {
    send("advertisement_form_view", {
      form_type:     "advertisement",
      page_title:    "Advertisement — Free Consultation Callback",
      page_location: url,
    });
  }, []);

  // ── Field interactions ────────────────────────────────────────────────────────

  /**
   * Call on onFocus for any field you want to track engagement on.
   * @param {string} fieldName  e.g. "name", "mobile"
   */
  const trackFieldFocus = useCallback((fieldName) => {
    send("advertisement_field_focus", {
      field_name: fieldName,
      form_type:  "advertisement",
    });
  }, []);

  // ── Form submission lifecycle ─────────────────────────────────────────────────

  /**
   * Fired every time the user clicks Submit (before validation result).
   * @param {boolean} hasErrors  — true if client-side validation failed
   */
  const trackSubmitAttempt = useCallback((hasErrors) => {
    send("advertisement_submit_attempt", {
      form_type:  "advertisement",
      has_errors: hasErrors,
    });
  }, []);

  /**
   * Fired after a successful API response.
   * @param {object} meta  — non-PII context
   */
  const trackSubmitSuccess = useCallback((meta = {}) => {
    send("advertisement_submit_success", {
      form_type: "advertisement",
      ...meta,
    });
  }, []);

  /**
   * Fired if the API call throws, or the API responds without the success code.
   * @param {string} reason  — e.g. "network", "server"
   */
  const trackSubmitError = useCallback((reason = "unknown") => {
    send("advertisement_submit_error", {
      form_type:    "advertisement",
      error_reason: reason,
    });
  }, []);

  return {
    trackFormView,
    trackFieldFocus,
    trackSubmitAttempt,
    trackSubmitSuccess,
    trackSubmitError,
  };
};

export default useAdvertisementAnalytics;